import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Phone, ArrowDown, ShieldCheck, BadgeCheck, Sparkles } from "lucide-react";
import InsuranceBadge from "@/components/landing/InsuranceBadge";

const EASE = [0.22, 1, 0.36, 1];

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1 -mb-1">
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero({ content }) {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 16 });

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const tel = `tel:${(content.phone || "").replace(/\D/g, "")}`;

  return (
    <section id="top" onMouseMove={onMouseMove}
      className="relative min-h-screen flex items-center overflow-hidden bg-grid pt-28 pb-20">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_70%_20%,rgba(37,99,235,0.18),transparent)]" />
      <div className="absolute -bottom-40 -left-40 h-[480px] w-[480px] rounded-full bg-royal/15 blur-[140px]" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14 items-center w-full">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
            className="inline-flex items-center gap-2 rounded-full border border-sky/30 bg-sky/10 px-4 py-1.5 mb-8">
            <Sparkles className="h-3.5 w-3.5 text-sky" />
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-sky" data-testid="hero-overline">
              {content.phone} — {content.service_area}
            </span>
          </motion.div>

          <h1 data-testid="hero-title" className="font-display font-extrabold tracking-tight leading-[1.04] text-4xl sm:text-5xl lg:text-7xl">
            <MaskedLine delay={0.1}>{content.hero_line1}</MaskedLine>
            <MaskedLine delay={0.24}>
              <span className="text-sky">{content.hero_line2}</span>
            </MaskedLine>
            <MaskedLine delay={0.38}>{content.hero_line3}</MaskedLine>
          </h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
            className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-slate-400" data-testid="hero-subtitle">
            {content.hero_sub}
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4">
            <a href="#quote" data-testid="hero-quote-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-royal px-8 py-4 font-display font-bold text-base hover:bg-royalhover transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_40px_rgba(37,99,235,0.45)]">
              Get a Free Estimate
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
            </a>
            <a href={tel} data-testid="hero-phone-cta"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-8 py-4 font-display font-bold text-base text-white hover:border-sky/60 hover:text-sky transition-colors duration-300">
              <Phone className="h-4 w-4" />
              Call {content.phone}
            </a>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1 }}
            className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-400">
            <InsuranceBadge text={content.insurance_badge} src={content.certificate_url} />
            <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-sky" /> Reliable &amp; Professional</span>
            <span className="inline-flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-sky" /> 100% Satisfaction Focused</span>
            <span className="inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-sky" /> Premium Materials</span>
          </motion.div>
        </div>

        <div className="lg:col-span-5 flex justify-center" style={{ perspective: 1200 }}>
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.92, rotate: -3 }}
            animate={{ opacity: 1, y: 0, scale: 1, rotate: 0 }}
            transition={{ duration: 1.1, delay: 0.35, ease: EASE }}
            className="relative"
          >
            <div className="absolute -inset-8 rounded-[3rem] bg-royal/35 blur-3xl animate-pulse-glow" aria-hidden="true" />
            <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="relative">
              <div className="rounded-[2rem] bg-white p-5 shadow-[0_40px_120px_rgba(11,19,43,0.8)] ring-1 ring-white/20"
                data-testid="hero-logo-panel">
                <img src="/logo.png" alt={`${content.business_name} logo`} className="w-full max-w-sm rounded-3xl" />
              </div>
              <motion.div style={{ transform: "translateZ(50px)" }}
                className="absolute left-1/2 -translate-x-1/2 top-full mt-4 rounded-2xl bg-navy/95 backdrop-blur border border-sky/30 px-5 py-3 shadow-xl whitespace-nowrap">
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-sky">{content.hero_overline}</p>
                <p className="font-display font-bold text-sm mt-0.5">{content.tagline}</p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
